import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useLanguage } from '../contexts/LanguageContext';
import { BUSINESS } from '../data/business';

export default function Contact() {
  const { t, language } = useLanguage();
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();

  return (
    <section id="contact" className="py-20 px-6 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div ref={leftRef} className={`max-w-2xl mx-auto ${leftVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h3 className="text-xl font-bold text-dark dark:text-white mb-6">
            {t('contact.talk')}
          </h3>

          <address
            className="not-italic space-y-5 mb-8"
            itemScope
            itemType="https://schema.org/LocalBusiness"
          >
            <meta itemProp="name" content={BUSINESS.name} />
            <meta itemProp="url" content={BUSINESS.url} />
            <meta itemProp="telephone" content={BUSINESS.phoneMachine} />
            <meta itemProp="email" content={BUSINESS.email} />

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-primary-tint dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary dark:text-accent-light" aria-hidden="true" />
              </div>
              <div>
                <div className="font-semibold text-dark dark:text-white mb-1">{t('contact.email')}</div>
                <a
                  href={BUSINESS.emailHref}
                  itemProp="email"
                  className="text-primary dark:text-accent-light hover:underline"
                >
                  {BUSINESS.email}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-primary-tint dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-primary dark:text-accent-light" aria-hidden="true" />
              </div>
              <div>
                <div className="font-semibold text-dark dark:text-white mb-1">{t('contact.phone')}</div>
                <a
                  href={BUSINESS.phoneHref}
                  itemProp="telephone"
                  className="text-primary dark:text-accent-light hover:underline"
                >
                  {BUSINESS.phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-primary-tint dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-primary dark:text-accent-light" aria-hidden="true" />
              </div>
              <div>
                <div className="font-semibold text-dark dark:text-white mb-1">{t('contact.location')}</div>
                <div
                  className="text-muted dark:text-gray-300"
                  itemProp="address"
                  itemScope
                  itemType="https://schema.org/PostalAddress"
                >
                  <span itemProp="postalCode">{BUSINESS.address.postalCode}</span>{' '}
                  <span itemProp="addressLocality">{BUSINESS.address.locality}</span>
                  <br />
                  <span itemProp="streetAddress">{BUSINESS.address.street}</span>
                  <meta itemProp="addressCountry" content={BUSINESS.address.countryCode} />
                </div>
                <div className="mt-3 rounded-xl overflow-hidden border border-line dark:border-gray-700">
                  <iframe
                    src="https://www.google.com/maps?q=Moselgasse+26,+1100+Wien&output=embed"
                    title={language === 'de' ? 'OptiAI auf Google Maps' : 'OptiAI on Google Maps'}
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-primary-tint dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <Globe className="w-5 h-5 text-primary dark:text-accent-light" aria-hidden="true" />
              </div>
              <div>
                <div className="font-semibold text-dark dark:text-white mb-1">
                  {language === 'de' ? 'Servicegebiet' : 'Service Area'}
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {BUSINESS.areaServed.map((country) => (
                    <span
                      key={country}
                      itemProp="areaServed"
                      className="inline-block px-2.5 py-1 bg-primary-tint dark:bg-blue-900/30 text-primary dark:text-accent-light text-sm rounded-full font-medium"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </address>

          <div className="bg-primary-tint dark:bg-blue-900/20 rounded-card p-6">
            <h4 className="font-bold text-dark dark:text-white mb-2">{t('contact.free.title')}</h4>
            <p className="text-muted dark:text-gray-300 leading-relaxed text-sm">
              {t('contact.free.text')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
